import { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useFormikContext } from 'formik';
import { useSnackbar } from 'notistack';
import { EventModel } from 'app/eventos/models/Evento';
import useEventoService from 'app/eventos/services/useEventoService';

type SelectedEvent = Pick<EventModel, 'id' | 'name'>;

interface EventFormValues {
  cliente: { id: number } | null;
  event?: SelectedEvent | null;
  nameEvent?: string;
}

interface Props {
  events: EventModel[];
  loading: boolean;
}

export const TransferReservationEventField = ({ events, loading }: Props) => {
  const { values, setValues, setFieldTouched, touched, errors, isSubmitting } =
    useFormikContext<EventFormValues>();
  const { createSimpleEvent } = useEventoService();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdEvents, setCreatedEvents] = useState<EventModel[]>([]);
  const selectedEvent = values.event ?? null;
  const availableEvents = [
    ...createdEvents.filter((created) => !events.some((event) => event.id === created.id)),
    ...events
  ];
  const options: SelectedEvent[] = selectedEvent && !availableEvents.some((event) => event.id === selectedEvent.id)
    ? [selectedEvent, ...availableEvents]
    : availableEvents;

  const selectEvent = (event: SelectedEvent | null) => {
    setValues((current) => ({ ...current, event, nameEvent: '' }));
  };

  const closeDialog = () => {
    if (!creating) setOpen(false);
  };

  const createEvent = async () => {
    if (creating || !values.cliente?.id || !name.trim()) return;

    setCreating(true);
    setError('');
    try {
      const event = await createSimpleEvent({
        business_client_id: values.cliente.id,
        name: name.trim()
      });
      setCreatedEvents((current) => [...current, event]);
      selectEvent(event);
      setOpen(false);
      enqueueSnackbar('Evento creado y seleccionado.', { variant: 'success' });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'No se pudo crear el evento.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex items-start gap-2">
      <Autocomplete<SelectedEvent>
        fullWidth
        value={selectedEvent}
        options={options}
        loading={loading}
        disabled={isSubmitting}
        getOptionLabel={(event) => `${event.name} (#${event.id})`}
        getOptionSelected={(option, value) => option.id === value.id}
        onChange={(_, event) => selectEvent(event)}
        onBlur={() => setFieldTouched('event', true)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Evento asociado"
            variant="outlined"
            error={!!touched.event && !!errors.event}
            helperText={touched.event && typeof errors.event === 'string' ? errors.event : undefined}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={20} />}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />
      <Tooltip title={values.cliente?.id ? 'Crear evento' : 'Seleccioná un cliente para crear un evento'} arrow>
        <span>
          <IconButton
            type="button"
            aria-label="Crear evento"
            size="small"
            style={{ marginTop: 10 }}
            disabled={!values.cliente?.id || isSubmitting}
            onClick={() => {
              setName('');
              setError('');
              setOpen(true);
            }}
          >
            <AddIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm" aria-labelledby="create-reservation-event-title">
        <DialogTitle id="create-reservation-event-title">Crear evento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            El evento se creará ahora y quedará seleccionado. Se asociará al viaje cuando guardes la reserva.
          </DialogContentText>
          <TextField
            id="create-reservation-event-name"
            autoFocus
            fullWidth
            required
            label="Nombre del evento"
            variant="outlined"
            value={name}
            disabled={creating}
            inputProps={{ maxLength: 255 }}
            error={!!error}
            helperText={error || 'El evento seguirá disponible aunque canceles la reserva.'}
            onChange={(event) => {
              setName(event.target.value);
              setError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                createEvent();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={closeDialog} disabled={creating}>Cancelar</Button>
          <Button
            type="button"
            color="primary"
            variant="contained"
            onClick={createEvent}
            disabled={creating || !name.trim() || !values.cliente?.id}
            startIcon={creating ? <CircularProgress size={18} /> : undefined}
          >
            {creating ? 'Creando...' : 'Crear y seleccionar'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
